import {
  Text,
  TextInput,
  Textarea,
  Checkbox,
  Select,
  MultiSelect,
  NumberInput,
  Group,
} from "@mantine/core";
import { IconTrash, IconPencil, IconSettings, IconCopy } from "@tabler/icons";
import _ from "lodash";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";

import RemoteSelect2 from "../../components/remoteSelect2";
import AgeRanger from "../../components/ageRanger";
import FileList from "../../components/fileList";
import LinkageList from "../../components/linkageList";

import { ProductCommissionTable } from "../../components/productCommissionTable";
import { ProductVariation } from "../../components/productVariation";
import ProductCheckList from "../../components/productCheckList";
import ActivityLog from "../../components/activityLog";
import DummyWidget from "../../components/dummyWidget";
import ProductCommissionScheduleTable from "../../components/productCommissionScheduleTable";

import {
  DATA_CURRENCY,
  DATA_PAY_MODES,
  DATA_JURISDICTION,
  DATA_PRODUCT_STATUS,
  DATA_PRODUCT_DEFAULT_CHECKLIST,
} from "../../data/options";

import { useCellRender } from "../../hooks/useCellRender";
import { useFormatter } from "../../hooks/useFomatter";
import React, { useEffect, useState } from "react";
import ProductFilter from "../../components/productFilter";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useLocation } from "react-router-dom";
import { useAuthUser } from "react-auth-kit";
import UniqueTextInput from "../../components/uniqueTextInput";
import AutoCodeInput from "../../components/autoCodeInput";
import { DatePicker } from "@mantine/dates";
import FormConfirmation from "../../components/formConfirmation";
import { getProductEditFormSchema } from "./productEditFormSchema";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";

const apiEntity = "product";
const productImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/product/downloadTemplate`;
const productImportExistingTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/product/downloadExistingTemplate`;

const PRODUCT_CONFIRM_FIELD_LABELS = {
  code: "Code",
  name: "English Name",
  cname: "Chinese Name",
  status: "Status",
  provider: "Provider",
  category: "Category",
  type: "Product Type",
};

const requiredField =
  (label) =>
  (value) => {
    if (value === null || value === undefined) return `${label} is required`;
    if (!String(value).trim()) return `${label} is required`;
    return null;
  };

const productValidate = {
  name: (value) => {
    const required = requiredField("English Name")(value);
    if (required) return required;
    return value.length < 4 ? "Name must have at least 4 letters" : null;
  },
  status: requiredField("Status"),
  provider: requiredField("Provider"),
  category: requiredField("Category"),
  type: requiredField("Product Type"),
  insuredWeeklyAge: (value) =>
    value >= 52 ? "Weekly Age must less than 52 week" : null,
};

function Product() {
  const [cellRender] = useCellRender();
  const [formatter] = useFormatter();
  const [filterQuery, setFilterQuery] = useState(null);

  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const { pathname: currentURL } = useLocation();
  const userRoleName = auth().userRole?.name?.toUpperCase() ?? "";

  useEffect(() => {
    const staffRoles = [
      "GI OPERATION",
      "OPERATION",
      "MANAGEMENT",
      "ACCOUNTING",
    ];
    if (staffRoles.includes(userRoleName)) return;
    if (!currentURL.includes("operation")) return;

    tabNav.replace({
      pathname: currentURL.replace("operation", "consultant"),
      label: "Product",
    });
  }, [userRoleName, currentURL, tabNav]);

  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "code",
        sortable: true,
        width: 200,
        cellRender: (col, data) => (
          <>
            <Group spacing={4} noWrap>
              <McaApprovalListBadge entity={data} />
            </Group>
            {cellRender.ProductCode(data)}
          </>
        ),
      },

      {
        field: "name",
        headerName: "Product",
        sortable: true,
        width: 400,
        cellRender: (col, data) => cellRender.ProductInfo(data),
        filterable: true,

        filter: {
          apiEntity: "product",
          field: "provider",
          label: "Provider",
          populate: {
            model: "Provider",
            select: "name cname code shortName",
            labelField: "code",
            sort: { code: 1 },
          },
          width: 300,
          height: 200,
        },
      },
      {
        field: "category",
        headerName: "Cateogry",
        sortable: true,
        filterable: true,
        filter: {
          apiEntity: "product",
          field: "category",
          label: "Product Cateogry",
          populate: {
            model: "ProductCategory",
            select: "name cname code",
            labelField: "name",
          },
          width: 300,
          height: 200,
        },
        cellRender: (col, data) => cellRender.ProductCategory(data.category),
      },
      {
        field: "type.name",
        headerName: "Type",
        sortable: true,
        filterable: true,

        filter: {
          apiEntity: "product",
          field: "type",
          label: "Product Type",
          width: 300,
          height: 300,

          populate: {
            model: "ProductType",
            select: "name cname code",
            labelField: "name",
            sort: { name: 1 },
          },
        },

        cellRender: (col, data) => cellRender.ProductType(data.type),
      },

      {
        field: "status",
        sortable: true,
        cellRender: (col, data) => cellRender.Status(data.status),

        filterable: true,
        filter: {
          apiEntity: "product",
          field: "status",
          label: "Status",
          width: 300,
          height: 200,
        },
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {
              //   color:"red"
            },
          },
        ],
        actionMenu: [
          {
            name: "copy",
            label: "Copy",
            icon: IconCopy,
            props: {
              // color: "red",
            },
          },
          {
            name: "delete",
            label: "Delete data",
            icon: IconTrash,
            props: {
              color: "red",
            },
          },
        ],
      },
    ],
    select: "code name cname status provider category type mcaRequestId mcaPending",
    searchableFields: [
      "code",
      "name",
      "cname",
      "status",
      "provider.code",
      "provider.cname",
      "provider.shortName",
      "type.name",
      "type.cname",
      "type.code",
    ],
  };

  const productInitialValues = {
    code: "",
    planCode: "",
    name: "",
    cname: "",
    status: "ACTIVE",
    category: null,
    type: null,
    provider: null,
    applicantAgeMin: 18,
    applicantAgeMax: 70,
    insuredAgeMin: 0,
    insuredAgeMax: 100,
    currency: "HKD",
    description: "",
    minInitialCharge: 0,
    variations: [],
    checkList: DATA_PRODUCT_DEFAULT_CHECKLIST,
  };

  const addFormSchema = {
    title: "New Product",
    description: "Please fill in the basic info to create a new product",
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "xl",
    },
    apiEntity,
    showSaveBar: false,
    initialValues: productInitialValues,
    validate: productValidate,
    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
          props: {
            variant: "default",
            defaultValue: "Basic Info",
            orientation: "vertical",
            style: { minHeight: "400px" },
          },
          steps: [
            { key: "s1", label: "Basic Info", icon: IconSettings },
            { key: "s2", label: "Confirm", icon: IconSettings },
          ],
        },
        {
          key: "section-basic",
          parent: "s1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Basic Info",
            description: "Please fill in the basic info of product",
            withBorder: false,
            ml: 10,
            mt: 0,
          },
        },
        {
          key: "basicInfo-g",
          parent: "section-basic",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [{ key: "basicInfo-g1", props: { span: 12 } }],
        },
        {
          key: "section-confirm",
          parent: "s2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Confirm",
            description:
              "Please confirm only if the provided information is correct.",
            withBorder: false,
            ml: 10,
            mt: 0,
          },
        },
      ],
      fields: [
        {
          name: "code",
          component: UniqueTextInput,
          parent: "basicInfo-g1",
          props: {
            label: "Code (Auto if empty)",
            placeholder: "",
            uniqueEntity: "product",
            errorMessage: "Product code is already exist.",
            mt: 0,
          },
        },
        {
          name: "category",
          component: RemoteSelect2,
          parent: "basicInfo-g1",
          props: {
            name: "category",
            apiEntity: "productCategory",
            required: true,
            label: "Category",
            placeholder: "",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name", "code"],
            clearable: true,
            sort: { by: "name", order: "asc" },
          },
        },
        {
          name: "type",
          component: RemoteSelect2,
          parent: "basicInfo-g1",
          props: {
            name: "type",
            apiEntity: "productType",
            required: true,
            label: "Product Type",
            placeholder: "",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) => `[${data.code}] ${data.name} ${data.cname}`,
            pageSize: 300,
            searchFields: ["name", "code", "cname"],
            clearable: true,
            sort: { by: "name", order: "asc" },
          },
        },
        {
          name: "provider",
          component: RemoteSelect2,
          parent: "basicInfo-g1",
          props: {
            name: "provider",
            apiEntity: "provider",
            required: true,
            label: "Provider",
            placeholder: "AIA",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) => `[${data.code}] ${data.name}`,
            searchFields: ["name", "code"],
            pageSize: 10000,
            sort: { by: "code", order: "asc" },
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            required: true,
            label: "English Name",
            placeholder: "",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "basicInfo-g1",
          props: {
            label: "Chinese Name",
            placeholder: "",
          },
        },
        {
          name: "status",
          component: Select,
          parent: "basicInfo-g1",
          props: {
            required: true,
            label: "Status",
            placeholder: "",
            data: DATA_PRODUCT_STATUS,
            defaultValue: "ACTIVE",
          },
        },
        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {
            validateOnMount: true,
            fieldLabels: PRODUCT_CONFIRM_FIELD_LABELS,
            successText: [
              "Please confirm only if the provided information is correct.",
            ],
          },
        },
      ],
    },
  };

  const editFormSchema = getProductEditFormSchema();

  const formSchema = {
    add: addFormSchema,
    edit: editFormSchema,
  };

  const onFilterChange = (query) => {
    setFilterQuery(query);
  };

  return (
    <>
      <PageList
        title={"Products"}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={formSchema}
        showImport={true}
        showTemplateDownload={true}
        templateDownloads={[
          {
            url: productImportTemplateUrl,
            text: "Download Blank Template",
          },
          {
            url: productImportExistingTemplateUrl,
            text: "Download Existing Products",
          },
        ]}
        initSort={{ by: "code", order: "asc" }}
        //   filterQuery={filterQuery}
        //   filter={{
        //     component: ProductFilter,
        //     props: {
        //       onChange: onFilterChange,
        //     },
        //   }}
      />
    </>
  );
}

export default Product;
